import { useMemo, useState, useCallback } from "react";
import { find, get, set, filter , map , findIndex , forEach , noop , sortBy} from "lodash";
import moment from "moment";
import html2pdf from "html2pdf.js";
import { notification } from "antd";

import { formatDisplayDate } from "@utils/helpers";
import { useCareCoordinationStore } from "@stores/useCareCoordinationStore";
import CareCoordinationService from "@services/care-coordination";
import { useTaskValidations } from "@utils/customHooks";
import { forkJoin } from "rxjs";
import { INPROGRESS, ACTIVE, CHECKBOX, RADIO, OTHER } from "./constants";
import {
  USER_ACTION_MODULE_MEMBER_DETAIL,
  USER_ACTION_TAB_CARE_MANAGEMENT,
  USER_ACTION_CARD_CARE_PLAN_SUMMARY,
  USER_ACTION_LOCATION_GENERATE_REPORT,
  CASE_STATUS_REASON_REPORT,
  USER_ACTION_DESCRIPTION_REPORT_DOWNLOAD,
  USER_ACTION_DESCRIPTION_REPORT_UPLOAD,
} from "@utils/constants";
import membersService from "@services/members.service";
import { useReferralsStore } from "@stores/referralsStore";
import { NOT_ASSIGNED} from "@utils/constants";

export function useProgramMetaData({ programName , programId}: { programName ?: string; patientId?: number; programId?: number; prgmGrpId?: number; prgmMetadata?: any }): any {
  const assignedPrograms = useCareCoordinationStore((state) => state.assignedPrograms);
  const {requireConsent, completionReasonMandatory, completionReasonRequired, isAutoUploadCPSRRequired ,isAutoUploadCPSRDefaultChecked , defaultProgramPeriod, additionalDetails , additionalDataIds , programTypeData , declineRequiredPrograms} = useMemo(() => {
    const program = find(assignedPrograms, (program) => {
      return programName ? program.name === programName : programId ? program.id === programId : false;
    });

    let _additionalDetails = program ? get(program, "prgmMetadata.createProgramExtraFields", []) : [];

    return {
      requireConsent: program && get(program, "prgmMetadata.consentRequired", false),
      completionReasonMandatory: program && get(program, "prgmMetadata.completionReasonMandatory", false),
      completionReasonRequired: program && get(program, "prgmMetadata.completionReasonRequired", false),
      isAutoUploadCPSRRequired : program && get(program, "prgmMetadata.isAutoUploadCPSRRequired", false),
      isAutoUploadCPSRDefaultChecked : program && get(program, "prgmMetadata.isAutoUploadCPSRDefaultChecked", false),
      defaultProgramPeriod: program && get(program, "prgmMetadata.defaultProgramPeriod", false),
      additionalDetails: _additionalDetails,
      additionalDataIds : _additionalDetails.map((d : any) => get(d, 'subtaskTempId')),
      programTypeData : map(assignedPrograms, (item : any) => {
        const type = item.name !== "TCM" && item.name !== "ER" ? CHECKBOX : RADIO;
        const index = findIndex(assignedPrograms, { name: item?.name });
        const displayOrder = item?.name === "TCM" ? 1 : type === RADIO ? index + 2 : index;
        return { label: item.description, value: item.id, type: type, displayOrder ,...item};
      }).concat({ label: OTHER, name: OTHER, value: OTHER, patientProgramId: 0, status : '', type: RADIO, displayOrder: 9999 }) || [],
      declineRequiredPrograms : filter(assignedPrograms , (program : any) => {
        return get(program, "prgmMetadata.isDeclineRequired", false);
      }).map((p) => {
        return {
          label: p.description, value: p.id , disableInDecline : p?.status === ACTIVE  , ...p
        }
      })
    };
  }, [assignedPrograms, programId, programName]);

  const disableCompleteButton = (reason: string) => {
    return completionReasonRequired && completionReasonMandatory ? !reason : false;
  };

  const noAdditionalData = useMemo(() => {
    return !((additionalDetails || []).length > 0)
  } , [additionalDetails])

  return { requireConsent, completionReasonMandatory, completionReasonRequired, isAutoUploadCPSRRequired , isAutoUploadCPSRDefaultChecked ,defaultProgramPeriod, additionalDetails,additionalDataIds, disableCompleteButton, noAdditionalData ,programTypeData , declineRequiredPrograms};
}

export function useProgramData({ latestProgramData }: any) {
  const programsData = useCareCoordinationStore((state) => state.dataForCareCoordination);

  const tasks = get(latestProgramData || programsData, "interventions[0].tasks", []);
  const patientProgramId = get(latestProgramData || programsData, "patientProgramId", null);
  const consentId = get(latestProgramData || programsData, "consentId", null);
  const interventions = get(latestProgramData || programsData, "interventions", []);
  const programDocs = get(latestProgramData || programsData, "docDetails", null);
  const completionReason = get(latestProgramData || programsData, "completionReason", null);

  return { tasks, patientProgramId, consentId, interventions, programDocs, completionReason };
}

export function useCompleteButtonData({ defaultProgramPeriod , interventions}: any) {
  const interventionStatus = get(interventions , '[0].status' , null);

  const updatedDischargeDate = get(find(interventions?.[0]?.interventionDetails, { label: 'Discharge Date' }), 'value', null);
  const {remainingDays , completedDaysPercent , isAutoClosure} = useMemo(() => {
    let remainingDays = 0;
    let completedDaysPercent = "0%";
    const currentDate = new Date();
    let currentFormattedDate = formatDisplayDate(currentDate, "", "reminderDateActualFormat");
    let dischargeFormattedDate = formatDisplayDate(currentDate, "", "reminderDateActualFormat");
    let autoClosureFormattedDate = formatDisplayDate(currentDate, "", "reminderDateActualFormat");
    if (interventionStatus === "Active") {
      if (updatedDischargeDate) {
        const dischargeDate = new Date(updatedDischargeDate);
        const autoClosureDate = new Date(updatedDischargeDate);
        autoClosureDate.setDate(autoClosureDate.getDate() + defaultProgramPeriod);
        autoClosureFormattedDate = formatDisplayDate(autoClosureDate, "", "reminderDateActualFormat");
        dischargeFormattedDate = formatDisplayDate(dischargeDate, "", "reminderDateActualFormat");
        if (autoClosureFormattedDate > currentFormattedDate && currentFormattedDate >= dischargeFormattedDate) {
          const completedDays = Math.round((currentDate.getTime() - dischargeDate.getTime()) / (1000 * 60 * 60 * 24));
          completedDaysPercent = Math.round((completedDays / defaultProgramPeriod) * 100) + "%";
          remainingDays = Math.round((autoClosureDate.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24));
          if (remainingDays < 0) {
            remainingDays = 0;
          }
        }
      }
    }
    return {remainingDays : remainingDays , completedDaysPercent : completedDaysPercent , isAutoClosure : (autoClosureFormattedDate > currentFormattedDate && currentFormattedDate >= dischargeFormattedDate ? !!defaultProgramPeriod : false)}
  },[defaultProgramPeriod, interventionStatus, updatedDischargeDate])
  return { remainingDays, completedDaysPercent, isAutoClosure };
}

export function useAdditionalData({ programName, patientId }: { programName: string; patientId: number }): any {
  const additionalData = useCareCoordinationStore((state) => state.additionalData);
  const setAdditionalData = useCareCoordinationStore((state) => state.setAdditionalData);
  const validations = useCareCoordinationStore((state) => state.validations);
  const { programId, prgmGrpId, prgmMetadata } = useProgram({ programName });
  const { additionalDetails } = useProgramMetaData({ programName, patientId, programId, prgmGrpId, prgmMetadata });

  const { getTaskvalidations, isAdditionalValidationSuccess } = useTaskValidations({ activeTask: {}, dependantSubTasks: [], additionalDetails, additionalData });

  additionalDetails.forEach((ad: any) => {
    ad.validation = getTaskvalidations(ad).disabledDateFunc;
  });

  const updateAdditionalDataInTask = useCallback(
    async ({ latestProgramData }: any) => {
      if (Object.keys(additionalData).length > 0) {
        const tasks = get(latestProgramData, "interventions[0].tasks", []);

        const patientProgramId = get(latestProgramData, "patientProgramId", null);

        const promises: any[] = [];

        const subtasks: any[] = [];
        tasks.forEach((t: any) => {
          return subtasks.push(...t.subtasks);
        });

        const additionalDataSubtaskIds = get(prgmMetadata, "createProgramExtraFields", []).map((s: any) => {
          return s.subtaskTempId;
        });

        const requiredSubtasks = filter(subtasks, (s: any) => additionalDataSubtaskIds.includes(s.subtaskTempId));

        const interventions = get(latestProgramData, "interventions[0].interventionDetails", []);

        tasks.forEach((t: any) => {
          t.subtasks.forEach((st: any) => {
            if (Object.keys(additionalData).includes(st.subtaskTempId)) {
              set(t, "status", INPROGRESS);
              set(st, "value", moment(additionalData[st.subtaskTempId]).utc(true).format());
            }
          });
        });
        requiredSubtasks.forEach((rs: any) => {
          const interv = find(interventions, { labelId: rs.attrId });
          set(interv, "value", moment(additionalData[rs.subtaskTempId]).utc().format());
        });

        promises.push(
          CareCoordinationService.updateTask({
            patientId: patientId,
            programId: programId,
            interventionId: tasks[0].intervId,
            status: INPROGRESS,
            taskId: tasks[0].id,
            value: tasks[0].value,
            taskCreateDate: tasks[0].taskCreateDate,
            taskEndDate: tasks[0].taskEndDate,
          })
        );

        requiredSubtasks.forEach((subt: any) => {
          if (additionalData[subt.subtaskTempId]) {
            promises.push(
              CareCoordinationService.updateSubTask({
                patientId: patientId,
                interventionId: tasks[0].intervId,
                taskId: tasks[0].id,
                subTaskId: subt?.id,
                value: moment(additionalData[subt.subtaskTempId]).utc(true).format(),
              })
            );
          }
        });

        const interventionPromises: any[] = [];
        requiredSubtasks.forEach((subt: any) => {
          if (subt.attrId) {
            interventionPromises.push(
              CareCoordinationService.updateIntervention({
                patientId: patientId,
                programId: programId,
                patientPgmId: patientProgramId,
                interventionId: tasks[0].intervId,
                detailId: subt.attrId,
                detailValue: moment(additionalData[subt.subtaskTempId]).utc(),
                programName: programName,
                subtaskTempId: subt.subtaskTempId,
              })
            );
          }
        });

        const result = forkJoin(...promises, ...interventionPromises);
        result.subscribe({
          next: (response: any) => {
            setAdditionalData({});
          },
          error: (error) => {},
        });
      } else {
        setAdditionalData({});
      }

      return latestProgramData;
    },
    [additionalData, patientId, prgmMetadata, programId, programName, setAdditionalData]
  );

  const getValidations = (inputData: any) => {
    const numValidations = inputData.validations.split(",");

    const allValidations = validations.filter((v: any) => numValidations.includes(v.id));

    const dependentValidation = allValidations.find((v: any) => v.name.includes("subtask"));

    return { dependentValidation: get(dependentValidation, "name") };
  };

  const disableInput = (inputData: any) => {
    const { dependentValidation } = getValidations(inputData);

    if (dependentValidation) {
      return !additionalData[dependentValidation.split("|")[1]];
    }
  };

  return { additionalDetails, disableInput, updateAdditionalDataInTask, isAdditionalValidationSuccess };
}

export function useConsent({ programName, patientId }: { programName: string; patientId: number }): any {
  const { patientProgramId, consentId } = useProgramData({});
  const [consentLoader, setConsentLoader] = useState(false);
  const programsData = useCareCoordinationStore((state) => state.dataForCareCoordination);
  const setProgramsData = useCareCoordinationStore((state) => state.setDataForCareCoordination);
  const [consent, setConsent] = useState<{ value: boolean; date: any }>({
    value: consentId ? true : false,
    date: formatDisplayDate(get(programsData, "enrollmentDate", ""), "createDateFormat", "defaultDateFormat"),
  });

  const { prgmGrpId, prgmMetadata, prgmGrpMetadata } = useProgram({ programName });

  const updateConsent = useCallback(
    (value, interventionStatus) => {
      if (interventionStatus === ACTIVE && !consentLoader) {
        if (!value) {
          setConsentLoader(true);
          CareCoordinationService.programEnroll({ prgmGrpId, prgmMetadata, patientId: patientId, prgmGrpMetadata, patientPrgmId: patientProgramId }).subscribe({
            next: (res: any) => {
              set(programsData, "consentId", get(res, "data.data.consentId", ""));
              setConsent({
                value: get(res, "data.data.consentId", "") ? true : false,
                date: formatDisplayDate(get(res, "data.data.enrollmentDate", moment()), "createDateFormat", "defaultDateFormat"),
              });
              setConsentLoader(false);
            },
            error: (error: any) => {
              setConsentLoader(false);
            },
          });
        } else {
          setConsentLoader(true);
          CareCoordinationService.programDisEnroll({ patientId, consentId }).subscribe({
            next: (res: any) => {
              setConsent({ value: false, date: "" });
              set(programsData, "consentId", null);
              setProgramsData(programsData);
              setConsentLoader(false);
            },
            error: (error: any) => {
              setConsentLoader(false);
            },
          });
        }
      }
    },
    [consentId, consentLoader, patientId, patientProgramId, prgmGrpId, prgmGrpMetadata, prgmMetadata, programsData, setProgramsData]
  );
  return { updateConsent, consentLoader, consent };
}

export function useProgram({ programName, programID }: { programName?: string; programID?: number }) {
  const assignedPrograms = useCareCoordinationStore((state) => state.assignedPrograms);

  const program = assignedPrograms.find((p: any) => (programID ? p.id === programID : p.name === programName));

  const programId = get(program, "id", null);
  const programDescription = get(program, "description");
  const programStatus = get(program, "status", ACTIVE);
  const prgmGrpId = get(program, "prgmGrpId", null);
  const prgmMetadata = get(program, "prgmMetadata", {});
  const prgmGrpMetadata = get(program, "prgmGrpMetadata", {});
  const programname = get(program, "name", "");
  const patientProgramId = get(program, "patientProgramId")

  return { programId, programDescription, programStatus, prgmGrpId, prgmMetadata, prgmGrpMetadata, programname, patientProgramId };
}


export function useCarePlanReport() {
  const [isReportLoading, setIsReportLoading] = useState(false);
  const [carePlanReportData, setCarePlanReportData] = useState<any>();
  const [carePlanProgramData, setCarePlanProgramData] = useState<any>();
  const [rules, setRule] = useState<any>([]);
  const [metaData, setMetaData] = useState({});
  const [isReportUploading , setIsReportUploading] = useState<boolean>(false);

  const store = useReferralsStore((store) => store);
  const { referrals } = store;

  const referralData = useMemo(() => {
    console.log("CarePlanReport.referralData")
    const { editable, nonEditable } = referrals;
    if (editable && nonEditable) {
      const _data = [...editable, ...nonEditable].filter((r) => !['sdoh program owner' , 'eol program owner'].includes(r.role_name.toLowerCase())).map((r) => {
        return {label : r.role_name, value : r.assignee ? r.assignee : NOT_ASSIGNED}
      });
      return sortBy(_data,  [(item) => item.label.toLowerCase()])
    }
    return [];
  }, [referrals]);

  const currentMember = membersService?.getCurrentMember;

  const getUserActionParams = (userActionDescription: any, reason: any) => {
    let props = {
      caseStatusReasonTxt: reason,
      actionModule: USER_ACTION_MODULE_MEMBER_DETAIL,
      actionTab: USER_ACTION_TAB_CARE_MANAGEMENT,
      actionCard: USER_ACTION_CARD_CARE_PLAN_SUMMARY,
      actionLocation: USER_ACTION_LOCATION_GENERATE_REPORT,
      actionDescription: userActionDescription,
    };
    return props;
  };

  const uploadReport = async ({ setIsDownloading = noop, setIsUploading = noop, metaData = {}, programs = [] }: any) => {
    try {
      console.log("Entering the Try Block");
      const { caseStatusReasonTxt, actionModule, actionTab, actionCard, actionLocation, actionDescription } = getUserActionParams(USER_ACTION_DESCRIPTION_REPORT_UPLOAD, CASE_STATUS_REASON_REPORT);
      membersService.updateStatus(currentMember?.patient_id, caseStatusReasonTxt, actionModule, actionTab, actionCard, actionLocation, actionDescription).subscribe({
        next: (v) => {
          console.log("updated status successfully");
        },
        error: (e) => {
          console.log("update status failed");
        },
      });
      setIsDownloading(true);

      //Page DOM chunks
      const contentChunks: any = [];

      const input: any = document.querySelectorAll(".ui-care-plan-report__page");
      const header: any = document.querySelector(".ui-care-plan-report__patient-info__detail");
      // const footer: any = document.querySelector(".ui-care-plan-report__footer__detail");

      //using page break logic
      const pageBreak = document.createElement("div");
      pageBreak.className = "html2pdf__page-break";

      //Intervention Headers
      // const InterventionTaskLabel = document.querySelector(".ui-care-plan-report__program__tasks__heading");
      // const InterventionTaskLabelHeight = InterventionTaskLabel?.getBoundingClientRect()?.height;
      const InterventionTasksDescriptionHeader = document.querySelector(".ui-care-plan-report__program__tasks__header");
      const InterventionTasksDescriptionHeaderHeight = InterventionTasksDescriptionHeader?.getBoundingClientRect()?.height;
      const careProgramSummaryReportDivider = document.querySelector(".ui-care-plan-report__division");

      //A4 page max content height in pixel
      const pageHeight = 820;
      // Page header height in pixel
      const pageHeaderHeight = 68;
      // Task header height in pixel
      const taskDescriptionHeight = 22;

      //Page Divider DOM
      const taskDividerBlackBorderClassName = "task-divider-black-border";
      const taskDividerBlackBorder = () => {
        const taskDivider = document.createElement("div");
        taskDivider.style.borderBottom = "1px solid #42526e";
        taskDivider.style.margin = "5px 0";
        taskDivider.className = taskDividerBlackBorderClassName;
        return taskDivider;
      };

      // Chunking care plan report into a4 page content
      forEach(input, (contentElement: any, index: any) => {
        if (contentElement) {
          const contentChildren = Array.from(contentElement.children);
          let currentPage: any = [];
          let currentHeight = 0;
          let programHeader: any;
          let pageNumber: any = 1;

          contentChildren.forEach((element: any) => {
            if (element.className === "ui-patient-notes") {
              const noteContentChildren = Array.from(element.children);
              noteContentChildren.forEach((noteElement: any) => {
                const elementHeight = noteElement.getBoundingClientRect().height;
                currentHeight += elementHeight;

                if (currentHeight <= pageHeight) {
                  currentPage.push(noteElement);
                } else {
                  contentChunks.push(currentPage);
                  // console.log("page number: ", pageNumber);
                  pageNumber += 1;
                  currentPage = [noteElement];
                  currentHeight = elementHeight + pageHeaderHeight;
                }
              });
            } else if (element.className === "ui-care-plan-report__program") {
              const programs = Array.from(element.children);
              programs.forEach((program: any) => {
                if (program.className === "ui-care-plan-report__program__title-value") {
                  const programInformationSectionHeight = program.getBoundingClientRect().height;
                  currentHeight += programInformationSectionHeight;

                  if (currentHeight <= pageHeight) {
                    currentPage.push(program);
                  } else {
                    contentChunks.push(currentPage);
                    // console.log("page number: ", pageNumber);
                    pageNumber += 1;
                    currentPage = [program];
                    currentHeight = programInformationSectionHeight + pageHeaderHeight;
                  }
                } else {
                  const programChildren = Array.from(program.children);
                  programChildren.forEach((programContainer: any) => {
                    const programGoalTaskContainer = Array.from(programContainer.children);
                    programGoalTaskContainer.forEach((goalsAndTask: any) => {
                      const goalsAndTaskContent = Array.from(goalsAndTask.children);
                      // goals and tasks array

                      goalsAndTaskContent.forEach((content: any) => {
                        if (content.className === "ui-care-plan-report__program__tasks__task-container") {
                          const taskContainer = Array.from(content.children);
                          let currentTask: any;

                          taskContainer.forEach((taskContents: any, taskContainerIndex: any) => {
                            if (taskContents.className === "ui-care-plan-report__program__tasks__body") {
                              const taskContainerWrapper1 = Array.from(taskContents.children);
                              taskContainerWrapper1.forEach((subTaskLi: any) => {
                                const subTaskElementHeight = subTaskLi.getBoundingClientRect().height;
                                currentHeight += subTaskElementHeight;

                                if (currentHeight <= pageHeight) {
                                  currentPage.push(subTaskLi);
                                } else {
                                  contentChunks.push(currentPage);

                                  //
                                  // console.log("page number: ", pageNumber);
                                  pageNumber += 1;

                                  //adding margin between program header and content of the body
                                  const programHeaderMargin = document.createElement("div");
                                  programHeaderMargin.style.marginTop = "8px";

                                  // If Intervention/Task header is required in new page we had this DOM into the newly created array -> DOM -> InterventionTaskLabel, DOM Height -> InterventionTaskLabelHeight
                                  currentPage = [programHeader, careProgramSummaryReportDivider, programHeaderMargin, InterventionTasksDescriptionHeader, currentTask, subTaskLi];
                                  currentHeight =
                                    subTaskElementHeight +
                                    programHeader?.getBoundingClientRect()?.height +
                                    programHeaderMargin?.getBoundingClientRect()?.height +
                                    pageHeaderHeight +
                                    InterventionTasksDescriptionHeaderHeight +
                                    taskDescriptionHeight;
                                }
                              });
                            } else if (taskContents.className === "ui-care-plan-report__program__notes") {
                              const taskNotesContainer = Array.from(taskContents.children);
                              taskNotesContainer.forEach((taskNote: any) => {
                                const taskNoteElementHeight = taskNote.getBoundingClientRect().height;
                                currentHeight += taskNoteElementHeight;

                                if (currentHeight <= pageHeight) {
                                  currentPage.push(taskNote);
                                } else {
                                  contentChunks.push(currentPage);
                                  // console.log("page number: ", pageNumber);
                                  pageNumber += 1;
                                  currentPage = [programHeader, careProgramSummaryReportDivider, taskNote];
                                  currentHeight =
                                    taskNoteElementHeight + pageHeaderHeight + programHeader?.getBoundingClientRect()?.height + careProgramSummaryReportDivider?.getBoundingClientRect()?.height;
                                }
                              });
                            } else {
                              // creating task description for next page continuation
                              if (taskContents.className === "ui-care-plan-report__program__tasks__body__task__header") {
                                if (taskContents?.children?.[0]?.className === "ui-care-plan-report__program__tasks__body__task__description") {
                                  // currentTask = taskContents?.children?.[0];
                                  const taskDescription = document.createElement("div");
                                  taskDescription.className = "ui-care-plan-report__program__tasks__body__task__description";
                                  const updatedTextDescription = taskContents?.children?.[0].textContent;
                                  taskDescription.textContent = updatedTextDescription + " (...Continued)";
                                  currentTask = taskDescription;
                                }
                              }
                              const taskElementHeight = taskContents.getBoundingClientRect().height;
                              currentHeight += taskElementHeight;

                              if (currentHeight <= pageHeight) {
                                currentPage.push(taskContents);
                              } else {
                                contentChunks.push(currentPage);
                                // console.log("page number: ", pageNumber);
                                pageNumber += 1;
                                //Adding Program header to the new page
                                currentPage = [programHeader, careProgramSummaryReportDivider, taskContents];
                                currentHeight =
                                  taskElementHeight + pageHeaderHeight + programHeader?.getBoundingClientRect()?.height + careProgramSummaryReportDivider?.getBoundingClientRect()?.height;
                              }
                            }
                            if (taskContainerIndex === taskContainer.length - 1) {
                              currentHeight += 10;
                              currentPage.push(taskDividerBlackBorder());
                            }
                          });
                        } else {
                          if (content.className === "ui-patient-notes") {
                            const programGoalNotes = Array.from(content.children);
                            programGoalNotes.forEach((goals: any) => {
                              const goalHeight = goals.getBoundingClientRect().height;
                              currentHeight += goalHeight;

                              if (currentHeight <= pageHeight) {
                                currentPage.push(goals);
                              } else {
                                contentChunks.push(currentPage);
                                // console.log("page number: ", pageNumber);
                                pageNumber += 1;
                                currentPage = [programHeader, careProgramSummaryReportDivider, goals];
                                currentHeight = goalHeight + pageHeaderHeight + programHeader?.getBoundingClientRect()?.height + careProgramSummaryReportDivider?.getBoundingClientRect()?.height;
                              }
                            });
                          } else {
                            const goalTaskElementHeight = content.getBoundingClientRect().height;
                            currentHeight += goalTaskElementHeight;

                            if (currentHeight <= pageHeight) {
                              currentPage.push(content);
                            } else {
                              contentChunks.push(currentPage);
                              // console.log("page number: ", pageNumber);
                              pageNumber += 1;
                              currentPage = [content];
                              currentHeight = goalTaskElementHeight + pageHeaderHeight;
                            }
                          }
                        }
                      });
                    });
                  });
                }
              });
            } else {
              //Capturing task header/description
              if (element.className === "ui-care-plan-report__title") {
                programHeader = element;
              }
              const elementHeight = element.getBoundingClientRect().height;
              currentHeight += elementHeight;

              if (currentHeight <= pageHeight) {
                currentPage.push(element);
              } else {
                contentChunks.push(currentPage);
                // console.log("page number: ", pageNumber);
                pageNumber += 1;
                currentPage = [element];
                currentHeight = elementHeight + pageHeaderHeight;
              }
            }
          });

          if (currentPage.length > 0) {
            contentChunks.push(currentPage);
          }

          // Page number logic
          // console.log("page number: ", pageNumber);
          let pageCounter = 0;
          contentChunks.reverse();
          while (pageCounter < pageNumber) {
            const pageContainer = document.createElement("div");
            pageContainer.textContent = `(Page ${pageNumber - pageCounter} of ${pageNumber})`;
            pageContainer.style.textAlign = "end";
            pageContainer.style.fontStyle = "italic";
            pageContainer.style.color = "#091E42";
            pageContainer.style.fontWeight = "400";

            if (contentChunks?.[pageCounter]?.[contentChunks?.[pageCounter].length - 1]?.className === taskDividerBlackBorderClassName) {
              contentChunks[pageCounter].push(pageContainer);
            } else {
              contentChunks[pageCounter].push(taskDividerBlackBorder());
              contentChunks[pageCounter].push(pageContainer);
            }
            pageCounter += 1;
          }
          contentChunks.reverse();
        }
      });

      contentChunks.forEach((ch: any, index: any) => {
        ch.unshift(header);
        // ch.push(footer);
        //using page break logic
        ch.push(pageBreak);
      });

      // console.log(contentChunks);
      const pdfOptions = {
        margin: 10,
        filename: "care-plan-summary-report.pdf",
        image: { type: "jpeg", quality: 1 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      };

      const pdfContainer = document.createElement("div");
      contentChunks.forEach((chunk: any, index: any) => {
        const page = document.createElement("div");
        page.style.marginTop = `${index * 2}px`;
        chunk.forEach((element: any) => {
          if (element) page.appendChild(element.cloneNode(true));
        });
        pdfContainer.appendChild(page);
      });

      const head = document.querySelector("head");
      let html = `
      <html>
        <head>
          ${head?.innerHTML}
        </head>
        <body>
          ${pdfContainer.outerHTML}
        </body>
      </html>
    `;

      const blob = await html2pdf().from(html).set(pdfOptions).outputPdf("blob");

      setIsDownloading(false);

      const getBase64 = (file: any) => {
        return new Promise((resolve) => {
          let baseURL: any = "";
          // Make new FileReader
          let reader = new FileReader();

          // Convert the file to base64 text
          reader.readAsDataURL(file);

          // on reader load somthing...
          reader.onload = () => {
            // Make a fileInfo Object
            baseURL = reader.result?.toString().replace("data:application/pdf;base64,", "");
            resolve(baseURL);
          };
        });
      };

      getBase64(blob)
        .then((result) => {
          // blob["base64"] = result;
          setIsDownloading(false);
          setIsReportUploading(true);
          CareCoordinationService.uploadDocsToAthena({
            practiceId: metaData?.practiceId,
            patientId: metaData?.athenaPatientId,
            departmentId: metaData?.departmentId,
            providerId: metaData?.providerId,
            attachmentContents: result,
            programs: programs,
          }).subscribe({
            next: (v) => {
              console.log("Uploaded");
              setIsReportUploading(false);
              notification.info({
                message: "File Uploaded Successfully.",
              });
            },
            error: (e) => {
              setIsReportUploading(false);
            },
          });
        })
        .catch((err) => {
          console.log(err);
          setIsDownloading(false);
        });
    } catch (e) {
      setIsDownloading(false);
    }
  };

  const downloadReport = async ({setIsDownloading = noop}) => {
    try {
      console.log("Entering the Try Block");
      const { caseStatusReasonTxt, actionModule, actionTab, actionCard, actionLocation, actionDescription } = getUserActionParams(USER_ACTION_DESCRIPTION_REPORT_DOWNLOAD, CASE_STATUS_REASON_REPORT);
      membersService.updateStatus(currentMember?.patient_id, caseStatusReasonTxt, actionModule, actionTab, actionCard, actionLocation, actionDescription).subscribe({
        next: (v) => {
          console.log("updated status successfully");
        },
        error: (e) => {
          console.log("update status failed");
        },
      });
      setIsDownloading(true);
      const input: any = document.querySelectorAll(".ui-care-plan-report__page");
      const header: any = document.querySelector(".ui-care-plan-report__patient-info__detail");
      // const footer: any = document.querySelector(".ui-care-plan-report__footer__detail");
      //using page break logic
      const pageBreak = document.createElement("div");
      pageBreak.className = "html2pdf__page-break";

      //Intervention Headers
      // const InterventionTaskLabel = document.querySelector(".ui-care-plan-report__program__tasks__heading");
      // const InterventionTaskLabelHeight = InterventionTaskLabel?.getBoundingClientRect()?.height;
      const InterventionTasksDescriptionHeader = document.querySelector(".ui-care-plan-report__program__tasks__header");
      const InterventionTasksDescriptionHeaderHeight = InterventionTasksDescriptionHeader?.getBoundingClientRect()?.height;
      const careProgramSummaryReportDivider = document.querySelector(".ui-care-plan-report__division");

      const pageHeight = 820;
      const contentChunks: any = [];
      const pageHeaderHeight = 68;
      const taskDescriptionHeight = 22;

      const taskDividerBlackBorderClassName = "task-divider-black-border";
      const taskDividerBlackBorder = () => {
        const taskDivider = document.createElement("div");
        taskDivider.style.borderBottom = "1px solid #42526e";
        taskDivider.style.margin = "5px 0";
        taskDivider.className = taskDividerBlackBorderClassName;
        return taskDivider;
      };

      forEach(input, (contentElement: any, index: any) => {
        if (contentElement) {
          const contentChildren = Array.from(contentElement.children);
          let currentPage: any = [];
          let currentHeight = 0;
          let programHeader: any;
          let pageNumber: any = 1;

          contentChildren.forEach((element: any) => {
            if (element.className === "ui-patient-notes") {
              const noteContentChildren = Array.from(element.children);
              noteContentChildren.forEach((noteElement: any) => {
                const elementHeight = noteElement.getBoundingClientRect().height;
                currentHeight += elementHeight;

                if (currentHeight <= pageHeight) {
                  currentPage.push(noteElement);
                } else {
                  contentChunks.push(currentPage);
                  // console.log("page number: ", pageNumber);
                  pageNumber += 1;
                  currentPage = [noteElement];
                  currentHeight = elementHeight + pageHeaderHeight;
                }
              });
            } else if (element.className === "ui-care-plan-report__program") {
              const programs = Array.from(element.children);
              programs.forEach((program: any) => {
                if (program.className === "ui-care-plan-report__program__title-value") {
                  const programInformationSectionHeight = program.getBoundingClientRect().height;
                  currentHeight += programInformationSectionHeight;

                  if (currentHeight <= pageHeight) {
                    currentPage.push(program);
                  } else {
                    contentChunks.push(currentPage);
                    // console.log("page number: ", pageNumber);
                    pageNumber += 1;
                    currentPage = [program];
                    currentHeight = programInformationSectionHeight + pageHeaderHeight;
                  }
                } else {
                  const programChildren = Array.from(program.children);
                  programChildren.forEach((programContainer: any) => {
                    const programGoalTaskContainer = Array.from(programContainer.children);
                    programGoalTaskContainer.forEach((goalsAndTask: any) => {
                      const goalsAndTaskContent = Array.from(goalsAndTask.children);
                      // goals and tasks array

                      goalsAndTaskContent.forEach((content: any) => {
                        if (content.className === "ui-care-plan-report__program__tasks__task-container") {
                          const taskContainer = Array.from(content.children);
                          let currentTask: any;

                          taskContainer.forEach((taskContents: any, taskContainerIndex: any) => {
                            if (taskContents.className === "ui-care-plan-report__program__tasks__body") {
                              const taskContainerWrapper1 = Array.from(taskContents.children);
                              taskContainerWrapper1.forEach((subTaskLi: any) => {
                                const subTaskElementHeight = subTaskLi.getBoundingClientRect().height;
                                currentHeight += subTaskElementHeight;

                                if (currentHeight <= pageHeight) {
                                  currentPage.push(subTaskLi);
                                } else {
                                  contentChunks.push(currentPage);

                                  //
                                  // console.log("page number: ", pageNumber);
                                  pageNumber += 1;

                                  //adding margin between program header and content of the body
                                  const programHeaderMargin = document.createElement("div");
                                  programHeaderMargin.style.marginTop = "8px";

                                  // If Intervention/Task header is required in new page we had this DOM into the newly created array -> DOM -> InterventionTaskLabel, DOM Height -> InterventionTaskLabelHeight
                                  currentPage = [programHeader, careProgramSummaryReportDivider, programHeaderMargin, InterventionTasksDescriptionHeader, currentTask, subTaskLi];
                                  currentHeight =
                                    subTaskElementHeight +
                                    programHeader?.getBoundingClientRect()?.height +
                                    programHeaderMargin?.getBoundingClientRect()?.height +
                                    pageHeaderHeight +
                                    InterventionTasksDescriptionHeaderHeight +
                                    taskDescriptionHeight;
                                }
                              });
                            } else if (taskContents.className === "ui-care-plan-report__program__notes") {
                              const taskNotesContainer = Array.from(taskContents.children);
                              taskNotesContainer.forEach((taskNote: any) => {
                                const taskNoteElementHeight = taskNote.getBoundingClientRect().height;
                                currentHeight += taskNoteElementHeight;

                                if (currentHeight <= pageHeight) {
                                  currentPage.push(taskNote);
                                } else {
                                  contentChunks.push(currentPage);
                                  // console.log("page number: ", pageNumber);
                                  pageNumber += 1;
                                  currentPage = [programHeader, careProgramSummaryReportDivider, taskNote];
                                  currentHeight =
                                    taskNoteElementHeight + pageHeaderHeight + programHeader?.getBoundingClientRect()?.height + careProgramSummaryReportDivider?.getBoundingClientRect()?.height;
                                }
                              });
                            } else {
                              if (taskContents.className === "ui-care-plan-report__program__tasks__body__task__header") {
                                if (taskContents?.children?.[0]?.className === "ui-care-plan-report__program__tasks__body__task__description") {
                                  // currentTask = taskContents?.children?.[0];
                                  const taskDescription = document.createElement("div");
                                  taskDescription.className = "ui-care-plan-report__program__tasks__body__task__description";
                                  const updatedTextDescription = taskContents?.children?.[0].textContent;
                                  taskDescription.textContent = updatedTextDescription + " (...Continued)";
                                  currentTask = taskDescription;
                                }
                              }
                              const taskElementHeight = taskContents.getBoundingClientRect().height;
                              currentHeight += taskElementHeight;

                              if (currentHeight <= pageHeight) {
                                currentPage.push(taskContents);
                              } else {
                                contentChunks.push(currentPage);
                                // console.log("page number: ", pageNumber);
                                pageNumber += 1;
                                //Adding Program header to the new page
                                currentPage = [programHeader, careProgramSummaryReportDivider, taskContents];
                                currentHeight =
                                  taskElementHeight + pageHeaderHeight + programHeader?.getBoundingClientRect()?.height + careProgramSummaryReportDivider?.getBoundingClientRect()?.height;
                              }
                            }
                            if (taskContainerIndex === taskContainer.length - 1) {
                              currentHeight += 10;
                              currentPage.push(taskDividerBlackBorder());
                            }
                          });
                        } else {
                          if (content.className === "ui-patient-notes") {
                            const programGoalNotes = Array.from(content.children);
                            programGoalNotes.forEach((goals: any) => {
                              const goalHeight = goals.getBoundingClientRect().height;
                              currentHeight += goalHeight;

                              if (currentHeight <= pageHeight) {
                                currentPage.push(goals);
                              } else {
                                contentChunks.push(currentPage);
                                // console.log("page number: ", pageNumber);
                                pageNumber += 1;
                                currentPage = [programHeader, careProgramSummaryReportDivider, goals];
                                currentHeight = goalHeight + pageHeaderHeight + programHeader?.getBoundingClientRect()?.height + careProgramSummaryReportDivider?.getBoundingClientRect()?.height;
                              }
                            });
                          } else {
                            const goalTaskElementHeight = content.getBoundingClientRect().height;
                            currentHeight += goalTaskElementHeight;

                            if (currentHeight <= pageHeight) {
                              currentPage.push(content);
                            } else {
                              contentChunks.push(currentPage);
                              // console.log("page number: ", pageNumber);
                              pageNumber += 1;
                              currentPage = [content];
                              currentHeight = goalTaskElementHeight + pageHeaderHeight;
                            }
                          }
                        }
                      });
                    });
                  });
                }
              });
            } else {
              if (element.className === "ui-care-plan-report__title") {
                programHeader = element;
              }
              const elementHeight = element.getBoundingClientRect().height;
              currentHeight += elementHeight;

              if (currentHeight <= pageHeight) {
                currentPage.push(element);
              } else {
                contentChunks.push(currentPage);
                // console.log("page number: ", pageNumber);
                pageNumber += 1;
                currentPage = [element];
                currentHeight = elementHeight + pageHeaderHeight;
              }
            }
          });

          if (currentPage.length > 0) {
            contentChunks.push(currentPage);
          }

          // Page number logic
          // console.log("page number: ", pageNumber);
          let pageCounter = 0;
          contentChunks.reverse();
          while (pageCounter < pageNumber) {
            const pageContainer = document.createElement("div");
            pageContainer.textContent = `(Page ${pageNumber - pageCounter} of ${pageNumber})`;
            pageContainer.style.textAlign = "end";
            pageContainer.style.fontStyle = "italic";
            pageContainer.style.color = "#091E42";
            pageContainer.style.fontWeight = "400";

            if (contentChunks?.[pageCounter]?.[contentChunks?.[pageCounter].length - 1]?.className === taskDividerBlackBorderClassName) {
              contentChunks[pageCounter].push(pageContainer);
            } else {
              contentChunks[pageCounter].push(taskDividerBlackBorder());
              contentChunks[pageCounter].push(pageContainer);
            }
            pageCounter += 1;
          }
          contentChunks.reverse();
        }
      });

      contentChunks.forEach((ch: any, index: any) => {
        ch.unshift(header);
        // ch.push(footer);
        //using page break logic
        ch.push(pageBreak);
      });

      const pdfOptions = {
        margin: 10,
        filename: "generated-pdf.pdf",
        image: { type: "jpeg", quality: 1 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      };

      const pdfContainer = document.createElement("div");
      contentChunks.forEach((chunk: any, index: any) => {
        const page = document.createElement("div");
        page.style.marginTop = `${index * 2}px`;
        chunk.forEach((element: any) => {
          if (element) page.appendChild(element.cloneNode(true));
        });
        pdfContainer.appendChild(page);
      });
      await html2pdf().from(pdfContainer).set(pdfOptions).save();
      setIsDownloading(false);
    } catch (e) {
      setIsDownloading(false);
    }
  }

  const fetchReportData = ({payload = {}, callback = noop} : any) => {
    setIsReportLoading(true);
    CareCoordinationService.fetchCarePlanReportInfo({ patientId: membersService?.getCurrentMember?.patient_id, ...payload }).subscribe({
      next: (v) => {
        const response = v?.data?.data;
        setCarePlanReportData(response?.carePlanReport);
        setCarePlanProgramData(response?.activePrograms?.program);
        setRule(response?.rules);
        setMetaData(response?.carePlanDetails);
        setIsReportLoading(false);
        callback();
      },
      error: (e) => {
        console.log(e);
        setIsReportLoading(false);
      },
    });
  }

  return {isReportUploading , uploadReport , downloadReport , fetchReportData , carePlanReportData , referralData , carePlanProgramData , rules , isReportLoading , metaData};
}